import React, { useEffect, useState } from 'react';
import { ThemeProvider, Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { theme } from '../../Medical_History_Forms/themeprovider';
import Grid from '@mui/material/Grid2';
import { convertUnderscoreToTitle, infoTextStyle, sectionTitleStyle } from '../../../utils/commonUtils';
import { useLoader } from '../../loaderProvider/loader';
import styles from '../oocyteRetrieval/oocyteRetrival.module.css';
import { getSurgicalSpermRetrieval, SurgicalSpermRetrievalType } from './spermRetrieval.helper';
import ImageLightbox from '../ImageLightbox';

interface LaparoscopyViewDetailsProps {
  record: number; 
  onBack: () => void;
  onEdit?: () => void; 
}

const SurgicalSpermRetrievalDetails: React.FC<LaparoscopyViewDetailsProps> = ({ record, onBack, onEdit }) => {
  const { showLoader, hideLoader } = useLoader();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [retrievalData, setRetrievalData] = useState<SurgicalSpermRetrievalType | null>(null);


  useEffect(() => {
    if (!record) return;

    showLoader();
    getSurgicalSpermRetrieval(record)
      .then((data) => setRetrievalData(data))
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Error fetching Oocyte Retrieval data');
        setRetrievalData(null); // Set to default on error
      })
      .finally(() => hideLoader());
  }, [record, showLoader, hideLoader]);

  

  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  const handleOpenDialog = (index: number) => {
    if (!retrievalData?.images) return;
    const urls = retrievalData.images.map(img => img.image);
    setSelectedImages(urls);
    setSelectedImageIndex(index);
    setOpenImageDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenImageDialog(false);
    setSelectedImages([]);
    setSelectedImageIndex(0);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4} mb={3}>
           
          <Box sx={{ width: '100%'}}>
            <Typography variant="h6" sx={{ ...infoTextStyle }}>
          Details
            </Typography>
          </Box>

          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Date</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              {retrievalData?.date  || '-'}
            </Typography>
          </Grid>
          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Time</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              {retrievalData?.time  || '-'}
            </Typography>
          </Grid>
          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Indication</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              { convertUnderscoreToTitle(retrievalData?.indication)  || '-'}
            </Typography>
          </Grid>
          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Clinician</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              {retrievalData?.clinician_name  || '-'} 
            </Typography>
          </Grid>
          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Specimen Type</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              { convertUnderscoreToTitle(retrievalData?.specimen_type) || '-'}
            </Typography>
          </Grid>

          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Method of Surgical sperm retrieval</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              {retrievalData?.method_of_surgical_sperm_retrieval || '-'}
            </Typography>
          </Grid>

          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Surgeon</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              { convertUnderscoreToTitle(retrievalData?.surgeon_name) || '-'}
            </Typography>
          </Grid>
          
          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Anesthetist</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              { retrievalData?.anesthetist_name || '-'}
            </Typography>
          </Grid>


          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Anesthesia</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              { convertUnderscoreToTitle(retrievalData?.anesthesia)  || '-'} 
            </Typography>
          </Grid>

          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Andrologist</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              { retrievalData?.andrologist_name  || '-'} 
            </Typography>
          </Grid>

          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Witness Andrologist</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              { retrievalData?.witness_andrologist_name  || '-'} 
            </Typography>
          </Grid>

          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Site</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              { convertUnderscoreToTitle(retrievalData?.site)  || '-'} 
            </Typography>
          </Grid>


        </Grid>


        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  height: '2rem',
                  background: 'rgba(250, 250, 250, 1)',
                  border: 'none',
                  '& .MuiTableCell-root': {
                    padding: '0.6rem 0.6rem',
                  },
                }}
              >
                {['Site', 'Sperm Count', 'Motility', 'Remark'].map((title, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      lineHeight: '1.45',
                      textAlign: 'left',
                      color: 'rgba(35, 35, 35, 1)',
                    }}
                  >
                    {title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow key={1}>
                <TableCell>Right</TableCell>
                <TableCell>{retrievalData?.right_sperm_count || '-'}</TableCell>
                <TableCell>{retrievalData?.right_motility || '-'}</TableCell>
                <TableCell>{retrievalData?.right_remark || '-'}</TableCell>
              </TableRow>
              <TableRow key={2}>
                <TableCell>Left</TableCell>
                <TableCell>{retrievalData?.left_sperm_count || '-'}</TableCell>
                <TableCell>{retrievalData?.left_motility || '-'}</TableCell>
                <TableCell>{retrievalData?.left_remark || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>


        <Grid container spacing={4} mb={3} mt={2}>
          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Complications</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              {retrievalData?.complications  || '-'} 
            </Typography>
          </Grid>

          <Grid size={3} sx={{ textAlign: 'left' }}>
            <Typography variant="body1" sx={sectionTitleStyle}>Remark</Typography>
            <Typography variant="body2" sx={infoTextStyle}>
              {retrievalData?.remarks  || '-'} 
            </Typography>
          </Grid>
        </Grid>


        {/* Image Gallery */}
        {retrievalData?.images && retrievalData.images.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" sx={sectionTitleStyle}>
              Images
            </Typography>
            <Grid container spacing={2}>
              {retrievalData.images.map((img, idx) => (
                <Grid key={img.id}>
                  <img
                    src={img.image}
                    alt={`Laporoscopy ${idx + 1}`}
                    style={{ width: '40px', height: '40px', cursor: 'pointer', borderRadius: 25 }}
                    onClick={() => handleOpenDialog(idx)}
                  />
                </Grid>
              ))}

            </Grid>
          </Box>
        )}


        <ImageLightbox
          open={openImageDialog}
          images={selectedImages}
          initialIndex={selectedImageIndex}  // Pass the index here
          onClose={handleCloseDialog}
          label="Preview"
        />
        
        <Box className={styles.customButtonDiv}>
          <button
            type="button"
            className={styles.resetButton}
            onClick={onBack}
          >
   Back to List
          </button>

          <button
            type="button"
            className={styles.customButton}
            onClick={onEdit}
          >
            <span style={{ fontWeight: 'bold' }}>
      Edit
            </span>
          </button>

        </Box>

      </Box>
    </ThemeProvider>
  );
};

export default SurgicalSpermRetrievalDetails;
